import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const DEFAULT_FILTERS = {
  enrollment_status: [],
  enrollment_status_mode: 'equals',
  trial_status: [],
  trial_status_mode: 'equals',
  consultation_status: [],
  consultation_status_mode: 'equals',
  lead_status_mode: 'equals',
  lead_status_values: [],
  trial_date_filter: 'all',
  trial_date_from: '',
  trial_date_to: '',
  create_date_filter: 'all',
  create_date_from: '',
  create_date_to: '',
  country: [],
  country_mode: 'equals',
  funnel_type: [],
  trial_slot: [],
  is_hot: '',
};

export function useLeads(searchParams) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState([]);
  const [trialPaymentFilter, setTrialPaymentFilter] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [pricingConfigs, setPricingConfigs] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [activeView, setActiveView] = useState(null);

  // Debounce timer
  const debounceRef = useRef(null);

  // Apply URL params on mount
  useEffect(() => {
    if (!searchParams) return;
    const leadStatusParam = searchParams.get('lead_status');
    const trialStatusParam = searchParams.get('trial_status');
    const enrollmentStatusParam = searchParams.get('enrollment_status');
    const consultationStatusParam = searchParams.get('consultation_status');
    const trialPaymentParam = searchParams.get('trial_payment');

    const searchParam = searchParams.get('search');
    if (searchParam) setSearchTerm(searchParam);
    if (leadStatusParam) setLeadStatusFilter([leadStatusParam]);
    if (trialPaymentParam) setTrialPaymentFilter([trialPaymentParam]);
    const funnelTypeParam = searchParams.get('funnel_type');
    const doctorIdParam = searchParams.get('doctor_id');
    if (funnelTypeParam) {
      setFilters(prev => ({ ...prev, funnel_type: [funnelTypeParam] }));
    }
    if (doctorIdParam) {
      setFilters(prev => ({ ...prev, funnel_type: ['Doctor Referral'], doctor_id: doctorIdParam }));
    }
    if (trialStatusParam || enrollmentStatusParam || consultationStatusParam) {
      setFilters(prev => ({
        ...prev,
        ...(trialStatusParam && { trial_status: [trialStatusParam] }),
        ...(enrollmentStatusParam && { enrollment_status: [enrollmentStatusParam] }),
        ...(consultationStatusParam && { consultation_status: [consultationStatusParam] }),
      }));
    }
  }, [searchParams]);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    if (leadStatusFilter.length > 0) params.set('lead_status_values', leadStatusFilter.join(','));
    if (filters.lead_status_mode !== 'equals') params.set('lead_status_mode', filters.lead_status_mode);
    if (trialPaymentFilter.length > 0) params.set('trial_payment_status', trialPaymentFilter.join(','));
    if (filters.trial_status.length > 0) params.set('trial_status', filters.trial_status.join(','));
    if (filters.trial_status_mode !== 'equals') params.set('trial_status_mode', filters.trial_status_mode);
    if (filters.enrollment_status.length > 0) params.set('enrollment_status', filters.enrollment_status.join(','));
    if (filters.enrollment_status_mode !== 'equals') params.set('enrollment_status_mode', filters.enrollment_status_mode);
    if (filters.consultation_status.length > 0) params.set('consultation_status', filters.consultation_status.join(','));
    if (filters.consultation_status_mode !== 'equals') params.set('consultation_status_mode', filters.consultation_status_mode);
    if (filters.country.length > 0) params.set('country', filters.country.join(','));
    if (filters.country_mode !== 'equals') params.set('country_mode', filters.country_mode);
    if (filters.funnel_type.length > 0) params.set('funnel_type', filters.funnel_type.join(','));
    if (filters.doctor_id) params.set('doctor_id', filters.doctor_id);
    if (filters.trial_slot.length > 0) params.set('trial_slot', filters.trial_slot.join(','));
    if (filters.is_hot === 'true' || filters.is_hot === 'false') params.set('is_hot', filters.is_hot);
    if (filters.trial_date_filter !== 'all') {
      params.set('trial_date_filter', filters.trial_date_filter);
      if (filters.trial_date_from) params.set('trial_date_from', filters.trial_date_from);
      if (filters.trial_date_to) params.set('trial_date_to', filters.trial_date_to);
    }
    if (filters.create_date_filter !== 'all') {
      params.set('create_date_filter', filters.create_date_filter);
      if (filters.create_date_from) params.set('create_date_from', filters.create_date_from);
      if (filters.create_date_to) params.set('create_date_to', filters.create_date_to);
    }
    return params;
  }, [searchTerm, leadStatusFilter, trialPaymentFilter, filters]);

  const fetchLeads = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.set('page', p);
      params.set('limit', limit);

      const response = await axios.get(`${API_URL}/api/leads?${params.toString()}`);
      const data = response.data;
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [page, limit, buildFilterParams]);

  // Debounced fetch on filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLeads(1);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm, leadStatusFilter, trialPaymentFilter, filters]);

  // Immediate fetch on page change
  const goToPage = useCallback((p) => {
    setPage(p);
    fetchLeads(p);
  }, [fetchLeads]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/associates`);
      setUsers(response.data);
    } catch (error) { /* ignore */ }
  }, []);

  const fetchBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pricing`);
      setPricingConfigs(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  }, []);

  const fetchSavedViews = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/saved-views`);
      setSavedViews(response.data);
    } catch (error) {
      console.error('Error fetching saved views:', error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchUsers();
    fetchBatches();
    fetchPricing();
    fetchSavedViews();
  }, [fetchUsers, fetchBatches, fetchPricing, fetchSavedViews]);

  const resetFilters = useCallback(() => {
    setLeadStatusFilter([]);
    setTrialPaymentFilter([]);
    setFilters({ ...DEFAULT_FILTERS });
    setActiveView(null);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const applyView = useCallback((view) => {
    const saved = view.filters || {};
    setFilters({
      enrollment_status: Array.isArray(saved.enrollment_status) ? saved.enrollment_status : (saved.enrollment_status && saved.enrollment_status !== 'all' ? [saved.enrollment_status] : []),
      enrollment_status_mode: saved.enrollment_status_mode || 'equals',
      trial_status: Array.isArray(saved.trial_status) ? saved.trial_status : (saved.trial_status && saved.trial_status !== 'all' ? [saved.trial_status] : []),
      trial_status_mode: saved.trial_status_mode || 'equals',
      consultation_status: Array.isArray(saved.consultation_status) ? saved.consultation_status : (saved.consultation_status && saved.consultation_status !== 'all' ? [saved.consultation_status] : []),
      consultation_status_mode: saved.consultation_status_mode || 'equals',
      lead_status_mode: saved.lead_status_mode || 'equals',
      lead_status_values: saved.lead_status_values || [],
      trial_date_filter: saved.trial_date_filter || 'all',
      trial_date_from: saved.trial_date_from || '',
      trial_date_to: saved.trial_date_to || '',
      create_date_filter: saved.create_date_filter || 'all',
      create_date_from: saved.create_date_from || '',
      create_date_to: saved.create_date_to || '',
      country: Array.isArray(saved.country) ? saved.country : (saved.country && saved.country !== 'all' ? [saved.country] : []),
      country_mode: saved.country_mode || 'equals',
      funnel_type: Array.isArray(saved.funnel_type) ? saved.funnel_type : (saved.funnel_type && saved.funnel_type !== 'all' ? [saved.funnel_type] : []),
      trial_slot: Array.isArray(saved.trial_slot) ? saved.trial_slot : (saved.trial_slot && saved.trial_slot !== 'all' ? [saved.trial_slot] : []),
      is_hot: saved.is_hot === true || saved.is_hot === 'true' ? 'true'
        : saved.is_hot === false || saved.is_hot === 'false' ? 'false' : '',
    });
    setActiveView(view.view_id);
  }, []);

  // Update a single lead in local state (for optimistic updates)
  const updateLeadLocal = useCallback((leadId, updates) => {
    setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, ...updates } : l));
  }, []);

  return {
    leads, loading, total, page, totalPages, limit,
    searchTerm, setSearchTerm,
    leadStatusFilter, setLeadStatusFilter,
    trialPaymentFilter, setTrialPaymentFilter,
    filters, setFilters,
    users, batches, pricingConfigs,
    savedViews, activeView, setActiveView,
    fetchLeads: () => fetchLeads(page),
    goToPage,
    resetFilters, applyView,
    fetchSavedViews,
    updateLeadLocal,
    buildFilterParams,
  };
}
